import { cn, Text } from "@aurem/ui";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useState } from "react";

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

interface Record {
  [key: string]: JsonValue;
}

interface TableResultRendererProps {
  data: Record[] | JsonValue;
  isStreaming?: boolean;
  totalRows?: number;
}

const isRecord = (value: JsonValue): value is Record => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const isArrayOfRecords = (value: JsonValue): value is Record[] => {
  return Array.isArray(value) && value.length > 0 && isRecord(value[0]);
};

const MAX_ROWS = 20;

interface NestedTableProps {
  data: Record[];
}

const NestedTable: FC<NestedTableProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (data.length === 0) {
    return (
      <Text size="xs" className="text-neutral-500">
        Empty
      </Text>
    );
  }

  const keys = Object.keys(data[0]);
  const displayData = data.slice(0, MAX_ROWS);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-neutral-500 font-medium"
      >
        {isExpanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <Text size="xs" className="text-neutral-500">
          {data.length} {data.length === 1 ? "item" : "items"}
        </Text>
      </button>

      {isExpanded && (
        <div className="ms-4 mt-2 border border-neutral-500/10 rounded-lg overflow-hidden">
          <table className="table-auto w-full text-xs">
            <thead>
              <tr>
                {keys.map((key) => (
                  <th
                    key={key}
                    className="border-b border-r border-neutral-500/10 last:border-e-0 px-2 py-1 text-start"
                  >
                    <Text
                      size="xs"
                      weight="medium"
                      className="text-neutral-500"
                    >
                      {key}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, i) => {
                const isLastRow = i === displayData.length - 1;
                return (
                  <tr key={i} className="bg-white">
                    {keys.map((key) => (
                      <td
                        key={key}
                        className={cn(
                          "border-r border-neutral-500/10 last:border-e-0 px-2 py-1",
                          !isLastRow && "border-b",
                        )}
                      >
                        <CellValue value={row[key]} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.length > MAX_ROWS && (
            <div className="px-2 py-1 bg-neutral-500/10 border-t border-neutral-500/10">
              <Text size="xs" className="text-neutral-500">
                Showing {MAX_ROWS} of {data.length} items
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface CellValueProps {
  value: JsonValue;
}

const CellValue: FC<CellValueProps> = ({ value }) => {
  if (isArrayOfRecords(value)) {
    return <NestedTable data={value} />;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Text size="xs" className="text-neutral-500">
          Empty
        </Text>
      );
    }
    if (value.length <= 3 && value.every((val) => typeof val !== "object")) {
      return (
        <Text size="xs" className="text-neutral-500">
          {value.join(", ")}
        </Text>
      );
    }
    return (
      <pre className="text-xs bg-neutral-500/10 p-1 rounded max-w-[200px] overflow-x-auto text-neutral-500">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (isRecord(value)) {
    return (
      <pre className="text-xs bg-neutral-500/10 p-1 rounded max-w-[200px] overflow-x-auto text-neutral-500">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (value === null || value === undefined) {
    return (
      <Text size="xs" className="text-neutral-500">
        null
      </Text>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Text size="xs" className="text-neutral-500">
        {String(value)}
      </Text>
    );
  }

  return (
    <Text size="xs" className="truncate max-w-[200px] block text-neutral-500">
      {String(value)}
    </Text>
  );
};

export const TableResultRenderer: FC<TableResultRendererProps> = ({
  data,
  isStreaming = false,
  totalRows,
}) => {
  const { t } = useI18n();

  if (Array.isArray(data) && data.length === 0 && totalRows !== undefined) {
    return (
      <Text size="sm" className="text-neutral-500">
        Total: {totalRows}
      </Text>
    );
  }

  if (Array.isArray(data) && data.length > 0 && isRecord(data[0])) {
    const records = data as Record[];
    const keys = Object.keys(records[0]);
    const displayData = records.slice(0, MAX_ROWS);

    return (
      <div className="space-y-2">
        <div className="overflow-x-auto border border-neutral-500/10 rounded-lg">
          <table className="table-auto w-full text-xs">
            <thead className="bg-neutral-500/10">
              <tr>
                {keys.map((key) => (
                  <th
                    key={key}
                    className="border-b border-r border-neutral-500/10 last:border-e-0 px-2 py-1 text-start"
                  >
                    <Text
                      size="xs"
                      weight="medium"
                      className="text-neutral-500"
                    >
                      {key}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, i) => {
                const isLastRow = i === displayData.length - 1;
                return (
                  <tr
                    key={i}
                    className="bg-white"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    {keys.map((key) => (
                      <td
                        key={key}
                        className={cn(
                          "border-r border-neutral-500/10 last:border-e-0 px-2 py-1",
                          !isLastRow && "border-b",
                        )}
                      >
                        <CellValue value={row[key]} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!isStreaming && records.length > MAX_ROWS && (
          <Text size="xs" className="text-neutral-500">
            {t("apps.admin.chatAI.ui.showingRows", {
              maxRows: MAX_ROWS,
              total: records.length,
            })}{" "}
            – {t("apps.admin.chatAI.ui.requestExportToViewAll")}
          </Text>
        )}
      </div>
    );
  }

  if (Array.isArray(data)) {
    const truncated = data.slice(0, MAX_ROWS);
    return (
      <div className="space-y-2">
        <pre className="text-xs bg-neutral-500/10 p-2 rounded whitespace-pre-wrap break-words overflow-x-auto">
          {JSON.stringify(truncated, null, 2)}
        </pre>

        {data.length > MAX_ROWS && (
          <Text size="xs" className="text-neutral-500">
            {t("apps.admin.chatAI.ui.showingRows", {
              maxRows: MAX_ROWS,
              total: data.length,
            })}{" "}
            – {t("apps.admin.chatAI.ui.requestExportToViewAll")}
          </Text>
        )}
      </div>
    );
  }
};
